// Create-task modal. Lane B.1.b replaced the single 负责人 dropdown with
// a full RACI picker. Cross-store variant toggle lands in B.1.c.

import { useState } from 'react';
import { VAVA_SEED } from '../lib/seed.js';
import { hasValidResponsible } from '../lib/owners.ts';
import { Icon } from './Icon.jsx';
import { RaciPicker } from './RaciPicker.jsx';

export const NewTaskModal = ({ onClose, onCreate, staff, tasks, prefillParent, prefillAssignee, prefillGroup, groups = [] }) => {
  // If prefillAssignee given, default that person as R. Else first non-admin active staff.
  const defaultR = prefillAssignee
    ? staff.find(s => s.id === prefillAssignee)
    : staff.find(s => s.role !== 'admin' && s.active);
  const [form, setForm] = useState({
    // No phantom default category. Grouping is driven by groupId now; leaving
    // this '门店运营' silently stamped every new task with a stale label that
    // maps to no group (and thus to nothing in 待办总览). Blank instead — a task
    // gets its category from the 分类 (group) it's filed under, or none.
    title: '', detail: '', category: '', priority: 'P1',
    owners: defaultR ? [{ staffId: defaultR.id, role: 'R', isPrimary: true }] : [],
    dueDate: '', recurring: '一次性',
    parentId: prefillParent || '',
    groupId: prefillGroup || ''
  });
  const [err, setErr] = useState('');
  const parentCandidates = tasks
    ? tasks.filter(t => !t.parentId && t.status !== '已完成' && t.status !== '已取消')
    : [];

  function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) return setErr('请填写待办标题');
    if (!hasValidResponsible({ owners: form.owners })) {
      return setErr('请至少添加 1 位负责人 (R)');
    }
    // Carry the first R as legacy assigneeId for any code still
    // reading the old shape; loadTasks() migration shim drops the
    // duplication on next read.
    const firstR = form.owners.find(o => o.role === 'R');
    onCreate({ ...form, assigneeId: firstR ? firstR.staffId : null });
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-labelledby="new-task-title"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2 className="modal-title" id="new-task-title">新建待办</h2>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 14 }}>
            <label className="vava-label">标题 *</label>
            <input className="vava-input" value={form.title} autoFocus
                   placeholder="例：本月内完成 RH 灯牌采购"
                   onChange={(e) => setForm({ ...form, title: e.target.value })}/>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="vava-label">详情</label>
            <textarea className="vava-textarea" value={form.detail}
                      placeholder="描述 · 预期交付物 · 成功标准"
                      onChange={(e) => setForm({ ...form, detail: e.target.value })}/>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="vava-label">负责人 · RACI *</label>
            <div style={{ background: 'var(--vava-bg)', border: '1px solid var(--vava-border)', borderRadius: 8, padding: 14 }}>
              <RaciPicker owners={form.owners} staff={staff}
                          onChange={(next) => setForm({ ...form, owners: next })}/>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label className="vava-label">截止日期</label>
              <input type="date" className="vava-input mono" value={form.dueDate}
                     onChange={(e) => setForm({ ...form, dueDate: e.target.value })}/>
            </div>
            <div>
              <label className="vava-label">分类</label>
              {groups && groups.length > 0 ? (
                <select className="vava-select" value={form.groupId}
                        onChange={(e) => setForm({ ...form, groupId: e.target.value })}>
                  <option value="">—</option>
                  {groups.filter(g => !g.archived)
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(g => (
                      <option key={g.id} value={g.id}>
                        {g.icon ? g.icon + ' ' : ''}{g.parentId ? '— ' : ''}{g.name}
                      </option>
                    ))}
                </select>
              ) : (
                <select className="vava-select" value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {VAVA_SEED.CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="vava-label">优先级</label>
              <select className="vava-select" value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {VAVA_SEED.PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="vava-label">周期性</label>
              <select className="vava-select" value={form.recurring}
                      onChange={(e) => setForm({ ...form, recurring: e.target.value })}>
                <option value="一次性">一次性</option>
                <option value="每日">每日</option>
                <option value="每周">每周</option>
                <option value="每两周">每两周</option>
                <option value="每月">每月</option>
              </select>
            </div>
            <div>
              <label className="vava-label">所属待办（可选）</label>
              <select className="vava-select" value={form.parentId}
                      onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
                <option value="">— 无（独立待办）—</option>
                {parentCandidates.map(p => (
                  <option key={p.id} value={p.id}>{p.title.slice(0, 40)}{p.title.length > 40 ? '...' : ''}</option>
                ))}
              </select>
            </div>
          </div>
          {err && <div className="login-error" style={{ marginBottom: 12 }}>⚠ {err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>取消</button>
            <button type="submit" className="btn btn-primary"><Icon name="plus"/>新建并通知</button>
          </div>
        </form>
      </div>
    </div>
  );
};
